import { memo, useCallback, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import { CurrencyProvider } from 'contexts/currencyContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { type TransportOption, isCustomFlightSavedItemData, isLeFlightSavedItemData } from 'tripPlanner/api/types'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import type { RefreshedFlight } from './useRefreshShortlistedFlights'
import useFlightShortlistExpiry from './useFlightShortlistExpiry'
import { isBookedFlightOption, parseSearchParams } from './flightShortlistUtils'
import { getCachedJourney } from './shortlistedJourneyCache'
import { didClickInteractableElement } from 'components/Common/NestableClickable/NestableClickable'
import { useChatStreamStatus } from '../../contexts/ChatStreamStatusContext'
import type { BookFlightResult } from './useBookShortlistedFlight'

const DAY_MONTH_YEAR_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
}

interface Props {
  option: TransportOption
  isBooking: boolean
  isSearching: boolean
  isViewingDetails?: boolean
  refreshedData?: RefreshedFlight
  onBookNow: (option: TransportOption) => void | Promise<BookFlightResult>
  onDelete: (option: TransportOption) => void
  onRefresh: () => void
  onViewBookedDetails?: (option: TransportOption) => void
  onViewUnbookedDetails?: (option: TransportOption, refreshedData?: RefreshedFlight) => void
}

const AirlineLogo = styled.img`
  height: ${rem(32)};
  width: ${rem(32)};
  object-fit: contain;
  flex-shrink: 0;
`

const CardContainer = styled(Sheet)<{ $isExpired: boolean }>`
  opacity: ${props => props.$isExpired ? 0.7 : 1};

  &.is-clickable {
    cursor: pointer;
  }

  &.has-le-flight-hover-border:hover {
    border-color: ${props => props.theme.palette.brand.primary.hover};
  }
`

const Separator = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin: 0;
  width: 100%;
`

interface LegData {
  airline?: string
  flightNumber?: string
  departureAirport?: string
  arrivalAirport?: string
  departureTime?: string
  arrivalTime?: string
  duration?: string
  layovers?: Array<{ airportCode?: string; airportName?: string; duration?: string }>
  carrierLogo?: string
}

function formatTimeDisplay(timeString: string): string {
  if (timeString.includes('T')) {
    const date = new Date(timeString)
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
  }
  return timeString
}

function formatOccupantsLabel(occupancies: Array<{ adults?: number; children?: number; infants?: number }> | undefined, intl: ReturnType<typeof useIntl>): string {
  if (!occupancies?.length) return ''
  const occ = occupancies[0]
  const adults = occ.adults ?? 1
  const children = occ.children ?? 0
  const parts: Array<string> = []

  parts.push(intl.formatMessage(
    {
      id: 'sdK1Uf7spfyoDSVQjFtg',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Passenger count for shortlisted flight card header',
    },
    { count: adults },
  ))

  if (children > 0) {
    parts.push(intl.formatMessage(
      {
        id: '4mwNYERvlKnis5GUVpUB',
        defaultMessage: '{count, plural, one {# child} other {# children}}',
        description: 'Children passenger count displayed in the shortlisted flight card header section',
      },
      { count: children },
    ))
  }

  return parts.join(', ')
}

interface FlightLegRowProps {
  leg: LegData
}

function FlightLegRow({ leg }: FlightLegRowProps) {
  const departureTime = leg.departureTime ? formatTimeDisplay(leg.departureTime) : '--:--'
  const arrivalTime = leg.arrivalTime ? formatTimeDisplay(leg.arrivalTime) : '--:--'
  const routeLabel = `${leg.departureAirport ?? ''}-${leg.arrivalAirport ?? ''}`
  const flightTimeRange = `${departureTime} - ${arrivalTime}`
  return (
    <Group direction="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between" fullWidth>
      <Group direction="horizontal" gap={24} verticalAlign="center" shrink={false}>
        {leg.carrierLogo && (
          <AirlineLogo src={leg.carrierLogo} alt={leg.airline ?? ''} />
        )}
        <Group direction="vertical" gap={2}>
          <Heading variant="heading6">{flightTimeRange}</Heading>
          <Caption variant="medium" colour="light-foreground-alt">{leg.airline ?? ''}</Caption>
        </Group>
        <Group direction="vertical" gap={2} shrink={false}>
          <BodyText variant="medium" weight="bold">{leg.duration ?? ''}</BodyText>
          <Caption variant="medium" colour="light-foreground-alt">{routeLabel}</Caption>
        </Group>
      </Group>
    </Group>
  )
}

function ShortlistedFlightCard({
  option,
  isBooking,
  isSearching,
  isViewingDetails = false,
  refreshedData,
  onBookNow,
  onDelete,
  onRefresh,
  onViewBookedDetails,
  onViewUnbookedDetails,
}: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const { isStreaming } = useChatStreamStatus()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const savedItemData = option.savedItemData
  const isLeFlightData = isLeFlightSavedItemData(savedItemData)
  const isCustomFlightData = isCustomFlightSavedItemData(savedItemData)
  const isSoldOut = isLeFlightData && (refreshedData?.isSoldOut ?? false)
  const effectiveExpiry = isLeFlightData
    ? refreshedData?.newExpiry ?? savedItemData.searchExpiry
    : undefined
  const { isExpired: timerExpired } = useFlightShortlistExpiry(effectiveExpiry)
  const isExpired = isLeFlightData && (isSoldOut || timerExpired)
  const isBooked = isBookedFlightOption(option)
  const shouldShowBookNow = !isExpired && !isBooked
  const shouldShowBookedTag = !shouldShowBookNow && isBooked

  const returnLeg = option.returnLeg
  const returnLegData = isLeFlightSavedItemData(returnLeg?.savedItemData) ? returnLeg.savedItemData : undefined
  const isReturn = !!returnLeg && !!returnLegData

  const hydratedDepartingJourney = useMemo(() => {
    const fromRefresh = refreshedData?.matchedDepartingJourney ?? refreshedData?.matchedJourney
    if (fromRefresh) {
      return fromRefresh
    }

    if (!isLeFlightData || !savedItemData.journeyKey) {
      return undefined
    }

    return getCachedJourney(savedItemData.journeyKey)?.journey
  }, [isLeFlightData, refreshedData?.matchedDepartingJourney, refreshedData?.matchedJourney, savedItemData?.journeyKey])

  const hydratedReturningJourney = useMemo(() => {
    const fromRefresh = refreshedData?.matchedReturningJourney
    if (fromRefresh) {
      return fromRefresh
    }

    if (!isLeFlightData || !returnLegData?.journeyKey) {
      return undefined
    }

    return getCachedJourney(returnLegData.journeyKey)?.journey
  }, [isLeFlightData, refreshedData?.matchedReturningJourney, returnLegData?.journeyKey])

  const canViewUnbookedDetails = useMemo(() => {
    if (isBooked || !isLeFlightData || !onViewUnbookedDetails || isViewingDetails || isSoldOut) {
      return false
    }

    if (isReturn) {
      return Boolean(hydratedDepartingJourney && hydratedReturningJourney)
    }

    return Boolean(hydratedDepartingJourney)
  }, [isBooked, isLeFlightData, onViewUnbookedDetails, isViewingDetails, isSoldOut, isReturn, hydratedDepartingJourney, hydratedReturningJourney])

  const canViewBookedDetails = useMemo(() => {
    if (!isBooked || !isLeFlightData || !onViewBookedDetails || isViewingDetails) {
      return false
    }

    const orderId = savedItemData.orderId ?? returnLegData?.orderId
    const orderFlightItemId = savedItemData.orderFlightItemId ?? returnLegData?.orderFlightItemId

    return Boolean(orderId && orderFlightItemId)
  }, [isBooked, isLeFlightData, isViewingDetails, onViewBookedDetails, returnLegData?.orderFlightItemId, returnLegData?.orderId, savedItemData])

  const canViewDetails = canViewBookedDetails || canViewUnbookedDetails

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleDelete = useCallback(() => {
    closeDropdown()
    onDelete(option)
  }, [closeDropdown, onDelete, option])

  const handleBookNow = useCallback(() => {
    onBookNow(option)
  }, [onBookNow, option])

  const handleViewBookedDetails = useCallback(() => {
    if (canViewBookedDetails && onViewBookedDetails) {
      onViewBookedDetails(option)
      return
    }

    if (!canViewUnbookedDetails || !onViewUnbookedDetails) {
      return
    }

    onViewUnbookedDetails(option, refreshedData)
  }, [canViewBookedDetails, onViewBookedDetails, option, canViewUnbookedDetails, onViewUnbookedDetails, refreshedData])

  const handleCardClick = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!canViewDetails || !(event.target instanceof HTMLElement)) {
      return
    }

    if (didClickInteractableElement(event.target, event.currentTarget)) {
      return
    }

    handleViewBookedDetails()
  }, [canViewDetails, handleViewBookedDetails])

  const handleCardKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (!canViewDetails || !(event.target instanceof HTMLElement)) {
      return
    }

    if (didClickInteractableElement(event.target, event.currentTarget)) {
      return
    }

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      handleViewBookedDetails()
    }
  }, [canViewDetails, handleViewBookedDetails])

  const bookNowLabel = intl.formatMessage({
    id: '4VTNHzs2wLQP5XngJurI',
    defaultMessage: 'Book now',
    description: 'Button label to book a shortlisted flight in the trip planner itinerary',
  })

  const checkingAvailabilityLabel = intl.formatMessage({
    id: 'MWzt6RxwpAuF9uQV3G55',
    defaultMessage: 'Checking availability...',
    description: 'Loading text shown while re-searching flight availability before booking',
  })

  const searchingNode = useMemo<ReactNode>(() => (
    <Group direction="horizontal" gap={8} verticalAlign="center">
      <LoadingSpinner size={16} inline />
      <Caption variant="medium" colour="light-foreground-alt">{checkingAvailabilityLabel}</Caption>
    </Group>
  ), [checkingAvailabilityLabel])

  const bookNowButton = useMemo<ReactNode>(() => (
    <TextButton kind="primary" size="small" onClick={handleBookNow} disabled={isBooking}>
      {bookNowLabel}
    </TextButton>
  ), [handleBookNow, isBooking, bookNowLabel])

  const bookingActionNode = useMemo<ReactNode>(() => {
    if (shouldShowBookNow) {
      return isSearching ? searchingNode : bookNowButton
    }

    if (shouldShowBookedTag) {
      return <BookedLabel />
    }

    return null
  }, [bookNowButton, isSearching, searchingNode, shouldShowBookNow, shouldShowBookedTag])

  const searchParams = useMemo(() =>
    isLeFlightData ? parseSearchParams(savedItemData.searchParams) : undefined,
  [isLeFlightData, savedItemData])

  const subtitle = useMemo(() => {
    if (!isLeFlightData && !isCustomFlightData) return ''

    if (!isLeFlightData) {
      if (savedItemData.departureAirport && savedItemData.arrivalAirport) {
        return intl.formatMessage(
          {
            id: 'pmeuxOyYKH5ZCNDyJkFw',
            defaultMessage: '{origin} to {destination}',
            description: 'Flight route label showing origin and destination airports in shortlisted flight card subtitle',
          },
          {
            origin: savedItemData.departureAirport,
            destination: savedItemData.arrivalAirport,
          },
        )
      }
      return ''
    }

    const parts: Array<string> = []

    if (searchParams?.itinerary?.length) {
      const firstLeg = searchParams.itinerary[0]
      const lastLeg = searchParams.itinerary[searchParams.itinerary.length - 1]
      const departDate = dateFormatter(firstLeg.departureDate, DAY_MONTH_YEAR_FORMAT)
      if (isReturn) {
        const returnDate = dateFormatter(lastLeg.departureDate, DAY_MONTH_YEAR_FORMAT)
        parts.push(`${departDate} - ${returnDate}`)
      } else {
        parts.push(departDate)
      }
      parts.push(intl.formatMessage(
        {
          id: 'pmeuxOyYKH5ZCNDyJkFw',
          defaultMessage: '{origin} to {destination}',
          description: 'Flight route label showing origin and destination airports in shortlisted flight card subtitle',
        },
        {
          origin: savedItemData.departureAirport ?? firstLeg.departureAirport,
          destination: savedItemData.arrivalAirport ?? firstLeg.arrivalAirport,
        },
      ))
    }

    const occupantsLabel = formatOccupantsLabel(searchParams?.occupancies, intl)
    if (occupantsLabel) {
      parts.push(occupantsLabel)
    }

    return parts.join(' · ')
  }, [dateFormatter, isLeFlightData, isCustomFlightData, savedItemData, searchParams, isReturn, intl])

  if (!isLeFlightData && !isCustomFlightData) return null

  const headerTitle = isReturn
    ? intl.formatMessage({
        id: 'mLIlOWUKrurIn4sOwr09',
        defaultMessage: 'Return flights',
        description: 'Header title for a return flight card in the shortlisted flights section',
      })
    : intl.formatMessage({
        id: 'bnPie_corRQMr-f_TMzG',
        defaultMessage: 'One-way flight',
        description: 'Header title for a one-way flight card in the shortlisted flights section',
      })

  const leFlightData = isLeFlightData ? savedItemData : undefined

  const outboundLeg: LegData = {
    airline: savedItemData.airline,
    flightNumber: savedItemData.flightNumber,
    departureAirport: savedItemData.departureAirport,
    arrivalAirport: savedItemData.arrivalAirport,
    departureTime: savedItemData.departureTime,
    arrivalTime: savedItemData.arrivalTime,
    duration: savedItemData.duration,
    layovers: leFlightData?.layovers,
    carrierLogo: savedItemData.carrierLogo ?? leFlightData?.carrierLogo,
  }

  const inboundLeg: LegData | undefined = isReturn ? {
    airline: returnLegData.airline,
    flightNumber: returnLegData.flightNumber,
    departureAirport: returnLegData.departureAirport,
    arrivalAirport: returnLegData.arrivalAirport,
    departureTime: returnLegData.departureTime,
    arrivalTime: returnLegData.arrivalTime,
    duration: returnLegData.duration,
    layovers: returnLegData.layovers,
    carrierLogo: returnLegData.carrierLogo,
  } : undefined

  const farePrice = isLeFlightData
    ? (isReturn ? savedItemData.farePrice : savedItemData.price)
    : undefined
  const priceCurrency = isLeFlightData
    ? refreshedData?.matchedJourney?.currencyCode ?? savedItemData.fareCurrencyCode ?? savedItemData.currencyCode
    : undefined
  const displayPrice = refreshedData?.newPrice ?? farePrice

  const deleteLabel = intl.formatMessage({
    id: 'tIqA2l1Ir8TxtJVcN5iz',
    defaultMessage: 'Delete',
    description: 'Dropdown menu item to delete a shortlisted flight from the trip planner',
  })

  const expiredMessage = intl.formatMessage({
    id: 'XtEObgroFhJOEg_T8Xs8',
    defaultMessage: 'This flight price has expired',
    description: 'Per-card banner message shown when an individual shortlisted flight price has expired',
  })

  const refreshLabel = intl.formatMessage({
    id: '7fxey21pp4EkuxxhENyy',
    defaultMessage: 'Refresh',
    description: 'Button to refresh an individual expired shortlisted flight price in trip planner',
  })

  const kebabMenu = (
    <>
      <IconButton
        size="small"
        kind="tertiary"
        onClick={toggleDropdown}
        ref={dropdownTriggerRef}
        disabled={isStreaming}
      >
        <LineEllipsisVIcon />
      </IconButton>
      <DropdownList
        size="S"
        open={isDropdownOpen}
        onClose={closeDropdown}
        triggerRef={dropdownTriggerRef}
        anchorRef={dropdownTriggerRef}
        placement="bottom-end"
        portaled
      >
        <ClickableListItem
          title={deleteLabel}
          size="medium"
          startIcon={<LineTrashAltIcon size="S" />}
          onClick={handleDelete}
        />
      </DropdownList>
    </>
  )

  const shouldShowBookedTagInReturnHeader = isReturn && shouldShowBookedTag
  const shouldShowBottomDivider = !shouldShowBookedTagInReturnHeader
  const bookingActionNodeInReturnFooter = shouldShowBookedTagInReturnHeader ? null : bookingActionNode
  const hasBookingFooter = isLeFlightData && (displayPrice != null || bookingActionNodeInReturnFooter !== null)
  const returnContent = (
    <Group direction="vertical" gap={16} fullWidth>
      <Group direction="horizontal" gap={8} verticalAlign="start" horizontalAlign="space-between" fullWidth>
        <Group direction="vertical" gap={4}>
          <Heading variant="heading6">{headerTitle}</Heading>
          <Caption variant="medium" colour="light-foreground-alt">{subtitle}</Caption>
        </Group>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {shouldShowBookedTagInReturnHeader && <BookedLabel />}
          {kebabMenu}
        </Group>
      </Group>

      <Separator />

      <FlightLegRow leg={outboundLeg} />
      <FlightLegRow leg={inboundLeg!} />

      {hasBookingFooter && <>
        {shouldShowBottomDivider && <Separator />}

        <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign={displayPrice != null ? 'space-between' : 'end'} fullWidth>
          {displayPrice != null && (
            <CurrencyProvider currency={priceCurrency}>
              <PriceRowPrice size="M" price={displayPrice} saleUnit="adult" />
            </CurrencyProvider>
          )}
          {bookingActionNodeInReturnFooter}
        </Group>
      </>}
    </Group>
  )

  const onewayContent = (
    <Group direction="horizontal" gap={16} verticalAlign="center" horizontalAlign="space-between" fullWidth>
      <FlightLegRow leg={outboundLeg} />

      {isLeFlightData && displayPrice != null && (
        <CurrencyProvider currency={priceCurrency}>
          <Group direction="vertical" gap={0} shrink={false}>
            <PriceRowPrice size="M" price={displayPrice} saleUnit="adult" />
          </Group>
        </CurrencyProvider>
      )}

      <Group direction="horizontal" gap={8} verticalAlign="center" shrink={false}>
        {isLeFlightData && bookingActionNode}
        {kebabMenu}
      </Group>
    </Group>
  )

  return (
    <CardContainer
      border="light-foreground"
      borderRadius="L"
      padding={16}
      $isExpired={isExpired}
      className={clsx({
        'is-clickable': canViewDetails,
        'has-le-flight-hover-border': isLeFlightData,
      })}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={canViewDetails ? 'button' : undefined}
      tabIndex={canViewDetails ? 0 : undefined}
    >
      <Group direction="vertical" gap={12} fullWidth>
        {isReturn ? returnContent : onewayContent}
        {isExpired && (
          <MessageBanner
            kind="warning"
            density="compact"
            icon={<LineInfoCircleIcon size="S" />}
            primaryActionButtonProps={{
              children: refreshLabel,
              onClick: onRefresh,
              size: 'small',
            }}
          >
            {expiredMessage}
          </MessageBanner>
        )}
      </Group>
    </CardContainer>
  )
}

export default memo(ShortlistedFlightCard)
